import { useState } from "react";
import { toast } from "sonner";
import { FileText, ClipboardList, ImagePlus, Loader2 } from "lucide-react";
import { api, API_URL } from "@/lib/api";

const KINDS = [
  { key: "before", label: "Fotos — Antes da obra", accept: "image/jpeg,image/png" },
  { key: "after", label: "Fotos — Pós obra", accept: "image/jpeg,image/png" },
  {
    key: "invoice",
    label: "Nota fiscal do serviço",
    accept: "image/jpeg,image/png,application/pdf",
  },
  {
    key: "approval_receipt",
    label: "Comprovante de aprovação",
    accept: "image/jpeg,image/png,application/pdf",
  },
  {
    key: "payment_receipt",
    label: "Comprovante de pagamento",
    accept: "image/jpeg,image/png,application/pdf",
  },
];

export const ExecutionBlock = ({ lead, onChanged, onOpenFile }) => {
  const execution = lead.payload?.execution || {};
  const [uploading, setUploading] = useState(null);

  const upload = async (kind, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(kind);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      await api.post(`/admin/leads/${lead.id}/execution-files?kind=${kind}`, form);
      toast.success("Arquivos anexados.");
      onChanged();
    } catch (e) {
      toast.error(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Falha ao enviar arquivo."
      );
    } finally {
      setUploading(null);
    }
  };

  return (
    <div
      className="mt-6 rounded-xl border border-slate-200 bg-white p-4"
      data-testid="execution-block"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
          Evidências da execução
        </p>
        <a
          href={`/resumo/${lead.id}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="execution-summary-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-petrol px-3.5 py-1.5 text-[0.65rem] font-semibold text-white transition-colors hover:bg-brand"
        >
          <ClipboardList className="h-3 w-3" />
          Resumo pós-obra
        </a>
      </div>
      <div className="mt-3 space-y-4">
        {KINDS.map((k) => {
          const items = execution[k.key] || [];
          return (
            <div key={k.key}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold text-petrol">{k.label}</p>
                <label
                  data-testid={`execution-upload-${k.key}`}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed border-brand/50 px-3 py-1.5 text-[0.65rem] font-semibold text-brand transition-colors hover:bg-brand/5"
                >
                  {uploading === k.key ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <ImagePlus className="h-3 w-3" />
                  )}
                  Anexar
                  <input
                    type="file"
                    accept={k.accept}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      upload(k.key, e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              {items.length > 0 ? (
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {items.map((f, i) => (
                    <button
                      key={f.path}
                      onClick={() => onOpenFile(f)}
                      title={`${f.name} — abrir em nova guia`}
                      data-testid={`execution-file-${k.key}-${i}`}
                      className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition-[border-color,box-shadow] duration-200 hover:border-brand hover:shadow-md"
                    >
                      {f.content_type?.startsWith("image/") ? (
                        <img
                          src={`${API_URL}/files/${f.path}`}
                          alt={f.name}
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                      ) : (
                        <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-slate-500">
                          <FileText className="h-5 w-5" />
                          <span className="w-full truncate text-[0.6rem]">{f.name}</span>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-1.5 text-[0.65rem] text-slate-400">Nenhum arquivo anexado.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
